import { useState } from 'react';
import { Search, Plus, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { optimizeImageUrl } from '@/lib/imageUtils';
import { useQuery } from '@tanstack/react-query';
import { searchProducts } from '@/data/productPicker';

interface Product {
  id: string;
  slug: string;
  title: string;
  subtitle?: string;
  image?: string;
  categories?: string[];
}

interface ProductPickerProps {
  selectedProducts: Product[];
  onToggleProduct: (product: Product) => void;
}

export function ProductPicker({ selectedProducts, onToggleProduct }: ProductPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['product-picker', searchQuery],
    queryFn: () => searchProducts(searchQuery),
    enabled: searchQuery.length >= 2,
    staleTime: 2 * 60 * 1000,
  });

  const isSelected = (product: Product) => {
    return selectedProducts.some(p => p.id === product.id);
  };

  return (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search for tools you currently use (e.g., KVCore, Chime)..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedProducts.map((product) => (
            <Badge
              key={product.id}
              variant="secondary"
              className="flex items-center gap-1.5 px-3 py-1.5 cursor-pointer hover:bg-secondary/80"
              onClick={() => onToggleProduct(product)}
            >
              <Check className="h-3 w-3" />
              {product.title}
              <button className="ml-1 text-muted-foreground hover:text-foreground">×</button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search Results */}
      {searchQuery.length >= 2 && (
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-4">Searching...</p>
          ) : products.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No products found. Try a different search term.
            </p>
          ) : (
            products.map((product) => {
              const selected = isSelected(product);
              return (
                <Card
                  key={product.id}
                  className={`p-3 cursor-pointer transition-all hover:shadow-md ${
                    selected ? 'ring-2 ring-primary bg-primary/5' : ''
                  }`}
                  onClick={() => onToggleProduct(product)}
                >
                  <div className="flex items-center gap-3">
                    {/* Product Image */}
                    <div className="w-12 h-12 rounded-md overflow-hidden bg-muted shrink-0">
                      {product.image ? (
                        <img
                          src={optimizeImageUrl(product.image, { width: 48, height: 48 })}
                          alt={product.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-lg">
                          📦
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{product.title}</h4>
                      {product.subtitle && (
                        <p className="text-xs text-muted-foreground truncate">{product.subtitle}</p>
                      )}
                    </div>

                    {/* Selection Indicator */}
                    <div className="shrink-0">
                      {selected ? (
                        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center">
                          <Check className="h-4 w-4" />
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full border-2 border-muted-foreground/30 flex items-center justify-center">
                          <Plus className="h-4 w-4 text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Helper Text */}
      {searchQuery.length < 2 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          Type at least 2 characters to search for products
        </p>
      )}
    </div>
  );
}
